import { useCurrentWorkspace } from '@/providers/WorkspaceProvider';
import { DashboardMonthlyAnalysis, useDashboardMonthlyAnalysis } from '@/hooks/api/2080_dashboard_monthly_analysis';
import { useDashboardSearchCondition } from '@/providers/DashboardSearchConditionProvider';
import { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import {
  Chart as ChartJS,
  LinearScale,
  CategoryScale,
  BarElement,
  PointElement,
  LineElement,
  Legend,
  Tooltip,
  LineController,
  BarController,
  ChartData,
  ChartOptions,
  Filler,
  ChartType,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import { MotionDiv } from '@/components/element/MotionDiv';
import {
  PurchaseDashboardMonthlyAnalysis,
  usePurchaseDashboardMonthlyAnalysis,
} from '@/hooks/api/2160_purchase_dashboard_monthly_analysis';

ChartJS.register(
  LinearScale,
  CategoryScale,
  BarElement,
  PointElement,
  LineElement,
  Legend,
  Tooltip,
  LineController,
  BarController,
  Filler
);

const chartOptions: ChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'top',
      align: 'center',
      reverse: true,
      labels: {
        usePointStyle: true,
        boxWidth: 5,
        boxHeight: 5,
      },
    },
  },
  interaction: {
    intersect: false,
    mode: 'index',
  },
  scales: {
    y: {
      stacked: true,
      border: {
        display: false,
        dash: [5, 5],
      },
      grid: {
        display: true,
        drawOnChartArea: true,
        drawTicks: false,
      },
      ticks: {
        display: true,
        padding: 10,
        color: '#b2b9bf',
        font: {
          size: 11,
          family: 'Open Sans',
          style: 'normal',
          lineHeight: 2,
        },
      },
    },
    x: {
      stacked: true,
      border: {
        display: false,
        dash: [5, 5],
      },
      grid: {
        display: true,
        drawOnChartArea: true,
        drawTicks: true,
      },
      ticks: {
        display: true,
        color: '#b2b9bf',
        padding: 10,
        font: {
          size: 11,
          family: 'Open Sans',
          style: 'normal',
          lineHeight: 2,
        },
      },
    },
  },
};

const createGradientStroke = (ctx: CanvasRenderingContext2D) => {
  const gradient = ctx.createLinearGradient(0, 230, 0, 50);
  gradient.addColorStop(1, 'rgba(203,12,159,0.2)');
  gradient.addColorStop(0.2, 'rgba(72,72,176,0.0)');
  gradient.addColorStop(0, 'rgba(203,12,159,0)');
  return gradient;
};

export const MonthlyAnalysis = () => {
  const { _w_ } = useCurrentWorkspace();
  const {
    purchaseOrdersDashboardSearchCondition: searchCondition,
    setPurchaseOrdersDashboardSearchCondition: setSearchCondition,
  } = useDashboardSearchCondition();
  const { data, isPending } = usePurchaseDashboardMonthlyAnalysis({ ...searchCondition, identifier: _w_!.identifier });

  return (
    <>
      <MotionDiv className="row">
        <div className="col-12">
          <div className="card mb-4">
            <div className="card-header pb-0 p-3">
              <h6 className="mb-0">月次分析サマリー（積上）</h6>
            </div>
            <div className="card-body p-3">
              {data && !isPending && <CumulativeMonthlyAnalysisChart d={data} />}
              {/*<canvas id='mixed-chart' className='chart-canvas' height='300px'></canvas>*/}
            </div>
          </div>
        </div>
      </MotionDiv>

      <MotionDiv className="row">
        <div className="col-12">
          <div className="card mb-4">
            <div className="card-header pb-0 p-3">
              <h6 className="mb-0">月次分析サマリー（月別）</h6>
            </div>
            <div className="card-body p-3">
              {data && !isPending && <MonthlyAnalysisChart d={data} />}
              {/*<canvas id='mixed-chart' className='chart-canvas' height='300px'></canvas>*/}
            </div>
          </div>
        </div>
      </MotionDiv>
    </>
  );
};

const CumulativeMonthlyAnalysisChart = ({ d }: { d: PurchaseDashboardMonthlyAnalysis }) => {
  const [chartData, setChartData] = useState<ChartData>({ datasets: [] });
  const chartRef = useRef<ChartJS>(null);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    const labels = d.target_year_month.map((ym) => {
      const month = moment(`${ym}01`).month();
      // return moment.monthsShort(month);
      return `${month + 1}月`;
    });

    setChartData({
      labels,
      datasets: [
        {
          type: 'line',
          label: '予算',
          tension: 0.1,
          pointRadius: 0,
          pointBackgroundColor: '#cb0c9f',
          borderColor: '#cb0c9f',
          borderWidth: 3,
          backgroundColor: createGradientStroke(chart.ctx),
          data: d.ordering_budget_cumulative_amounts,
          fill: true,
        },
        {
          type: 'bar',
          label: d.purchase_order_phase_labels.completed, // 契約終了
          backgroundColor: 'rgba(69, 104, 205, 1)',
          data: d.completed_cumulative_amounts,
          maxBarThickness: 30,
        },
        {
          type: 'bar',
          label: d.purchase_order_phase_labels.contracting, // 契約中
          backgroundColor: 'rgba(69, 104, 205, 0.8)',
          data: d.contracting_cumulative_amounts,
          maxBarThickness: 30,
        },
        {
          type: 'bar',
          label: d.purchase_order_phase_labels.preparing, // 稟議準備
          backgroundColor: 'rgba(116, 182, 248, 0.4)',
          data: d.preparing_cumulative_amounts,
          maxBarThickness: 30,
        },
      ],
    });
  }, [d]);

  return <Chart ref={chartRef} type={'bar'} data={chartData} options={chartOptions} style={{ height: 300 }} />;
};

const MonthlyAnalysisChart = ({ d }: { d: PurchaseDashboardMonthlyAnalysis }) => {
  const [chartData, setChartData] = useState<ChartData>({ datasets: [] });
  const chartRef = useRef<ChartJS>(null);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    const labels = d.target_year_month.map((ym) => {
      const month = moment(`${ym}01`).month();
      // return moment.monthsShort(month);
      return `${month + 1}月`;
    });

    setChartData({
      labels,
      datasets: [
        {
          type: 'line',
          label: '予算',
          tension: 0.1,
          pointRadius: 0,
          pointBackgroundColor: '#cb0c9f',
          borderColor: '#cb0c9f',
          borderWidth: 3,
          backgroundColor: createGradientStroke(chart.ctx),
          data: d.ordering_budget_amounts,
          fill: true,
        },
        {
          type: 'bar',
          label: d.purchase_order_phase_labels.completed, // 契約終了
          backgroundColor: 'rgba(69, 104, 205, 1)',
          data: d.completed_amounts,
          maxBarThickness: 30,
        },
        {
          type: 'bar',
          label: d.purchase_order_phase_labels.contracting, // 契約中
          backgroundColor: 'rgba(69, 104, 205, 0.8)',
          data: d.contracting_amounts,
          maxBarThickness: 30,
        },
        {
          type: 'bar',
          label: d.purchase_order_phase_labels.preparing, // 稟議準備
          backgroundColor: 'rgba(116, 182, 248, 0.4)',
          data: d.preparing_amounts,
          maxBarThickness: 30,
        },
      ],
    });
  }, [d]);

  return <Chart ref={chartRef} type={'bar'} data={chartData} options={chartOptions} style={{ height: 300 }} />;
};
